<template>
  <section class="faq-section" :id="blockId || undefined">
    <div class="container">
      <div class="faq-grid">
        <section-title
          data-aos="fade-right"
          :kicker="kicker"
          :title="title"
          :lede="lede"
          align="left"
        />

        <div class="faq-ledger">
          <div class="accordion" :id="accordionId">
            <div v-for="(item, index) in items" :key="index" class="accordion-item" data-aos="fade-up">
              <h2 class="accordion-header" :id="accordionId + '-h-' + index">
                <button
                  class="accordion-button"
                  :class="{ collapsed: index !== 0 }"
                  type="button"
                  data-bs-toggle="collapse"
                  :data-bs-target="'#' + accordionId + '-c-' + index"
                  :aria-expanded="index === 0 ? 'true' : 'false'"
                  :aria-controls="accordionId + '-c-' + index"
                >
                  <span class="faq-num" aria-hidden="true">{{ String(index + 1).padStart(2, '0') }}</span>
                  <span class="faq-q">{{ item.q }}</span>
                  <span class="disclosure-mark" aria-hidden="true"></span>
                </button>
              </h2>
              <div
                :id="accordionId + '-c-' + index"
                class="accordion-collapse collapse"
                :class="{ show: index === 0 }"
                :aria-labelledby="accordionId + '-h-' + index"
                :data-bs-parent="'#' + accordionId"
              >
                <div class="accordion-body">{{ item.a }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* The one FAQ on the site. Every page renders its questions through this, so
   the ledger, the numbering and the marks cannot drift between them.

   Layout: the heading rail sits on the left and the ledger runs the full
   height of the section on the right, so the section reads as one list rather
   than a title stacked over a boxed card. The heading itself is SectionTitle,
   never a style block declared here. */
.faq-section {
  background: $ground-tint;
}

.faq-grid {
  display: grid;
  gap: 2.5rem;

  @media (min-width: 992px) {
    gap: 5rem;
    grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  }
}

/* Ledger rows: rules, not a card. Bootstrap's accordion chrome is stripped
   back to bare type so the numbering and the rules carry the structure. */
.faq-ledger {
  min-width: 0;

  .accordion {
    --bs-accordion-bg: transparent;
    --bs-accordion-border-color: transparent;
    --bs-accordion-border-radius: 0;
    --bs-accordion-inner-border-radius: 0;
    --bs-accordion-btn-focus-box-shadow: none;
    --bs-accordion-active-bg: transparent;
    --bs-accordion-active-color: #{$primary};
  }

  .accordion-item {
    background: transparent;
    border: 0;
    border-top: 1px solid $line-blue;

    &:last-child {
      border-bottom: 1px solid $line-blue;
    }
  }

  .accordion-header {
    margin-bottom: 0;
  }

  .accordion-button {
    align-items: start;
    background: transparent;
    box-shadow: none;
    color: $text-heading;
    display: grid;
    font-family: $headings-font-family;
    font-size: 1.05rem;
    font-weight: 600;
    gap: 0 1.1rem;
    grid-template-columns: 2.1rem minmax(0, 1fr) 0.85rem;
    letter-spacing: -0.01em;
    line-height: 1.4;
    padding: 1.5rem 0;
    text-align: left;

    /* Bootstrap's built-in chevron is replaced by the plus/minus mark. A
       pseudo-element still generates a box in a grid container, so it is
       switched off outright rather than merely stripped of its icon. */
    &::after {
      content: none;
      display: none;
    }

    &:focus,
    &:focus-visible {
      box-shadow: none;
      outline: none;
    }

    &:hover {
      color: $primary;
    }
  }

  .faq-num {
    color: $text-muted;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    padding-top: 0.3rem;
    transition: color $dur-quick $ease;
  }

  .faq-q {
    min-width: 0;
  }

  /* Colour and placement only. The mark's geometry and its plus/minus mechanic
     are the shared `.disclosure-mark` in _utilities.scss, which is also what
     turns it into a minus, off the aria-expanded Bootstrap keeps on the button
     above it. Declaring either here would be the second copy of the mark. */
  .disclosure-mark {
    color: $text-muted;
    margin-top: 0.3rem;
  }

  .accordion-button:not(.collapsed) {
    color: $primary;

    .faq-num,
    .disclosure-mark {
      color: $primary;
    }
  }

  /* Answer hangs under the question, clear of the numeral column. */
  .accordion-body {
    color: $text-label;
    font-size: 0.95rem;
    line-height: 1.7;
    padding: 0 2rem 1.6rem 3.2rem;
  }

  @media (max-width: 575.98px) {
    .accordion-body {
      padding-left: 0;
      padding-right: 0.5rem;
    }
  }
}
</style>

<script>
import SectionTitle from '@/Components/SectionTitle.vue';

export default {
  components: {
    SectionTitle
  },

  props: {
    kicker: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      required: true
    },
    lede: {
      type: String,
      default: ''
    },
    items: {
      type: Array,
      required: true
    },
    /* Required rather than defaulted. The id is what keeps one ledger
       independent of another: it is the `data-bs-parent` every panel names, so
       two lists sharing it would open and close each other. Every page names its
       own, and a shared default was a silent way to collide. */
    accordionId: {
      type: String,
      required: true
    },
    blockId: {
      type: String,
      default: ''
    }
  }
};
</script>
